import { useCallback, useEffect, useRef, useState } from 'react';
import type { AgentTurn } from '@integrate/contracts';
import { MAX_REALTIME_SESSION_MS } from '@integrate/contracts';
import {
  RealtimeConversation,
  type ConversationEnd,
  type OpenVoiceSocket,
} from '../voice/realtime-session.js';
import type { RealtimeAudioEnvironment } from '../voice/realtime-audio.js';

/**
 * The Talk control, and the strip of state a live conversation puts on screen.
 *
 * A SECOND BUTTON BESIDE DICTATE, NOT A REPLACEMENT FOR IT, and the distinction is worth stating
 * because they look alike. Dictation composes a precise instruction you READ before sending — "a
 * mis-transcribed instruction posted straight to a metered model is worse than one the person reads
 * first". This is a conversation: it answers back, it asks when something is unclear, and it can be
 * interrupted. The first is for the sentence you already have in your head; the second is for the
 * forty decisions of a workshop, where typing each one is the clerical act the feature exists to
 * remove.
 *
 * WHAT IT SHOWS WHILE LIVE, and each of the four is here for a reason rather than for symmetry:
 *
 *  - that it is listening, because an open microphone somebody has forgotten about is the risk this
 *    feature carries and a control they cannot see is one they cannot stop;
 *  - the time against the ceiling, because the session WILL end at ten minutes and being cut off
 *    mid-sentence with no warning reads as a fault rather than as a limit;
 *  - a mute, because a workshop has moments that are not for the model — and it is a real mute: the
 *    audio stops leaving the browser, not merely being acted on at the far end;
 *  - the partial transcript, because a spoken answer that leaves no text behind gives a reader
 *    scrolling back nothing at all until the turn is written down.
 */
export interface VoiceConversation {
  status: 'idle' | 'opening' | 'live';
  muted: boolean;
  error: string;
  /** What the person said, and what the model is saying, for the line above the composer. */
  heard: string;
  said: string;
  elapsedMs: number;
  start(): void;
  stop(): void;
  toggleMute(): void;
}

/**
 * A HOOK RATHER THAN A COMPONENT, because the two things it draws belong in two places.
 *
 * A live conversation shows its STATE above the composer — listening, the clock against the ceiling,
 * the mute, the partial transcript — and its CONTROL at the bottom, in the row with Ask, because that
 * is where every other thing you press lives. A component that rendered both could only put them
 * together, and rendering it twice would open two conversations.
 *
 * So the session lives here and the panel decides where each half goes.
 */
export function useVoiceConversation({
  projectId,
  scenarioId,
  onTurn,
  audio,
  openSocket,
}: {
  projectId: string;
  /** The scenario the conversation is held IN. The model is never offered the field. */
  scenarioId: string | null;
  /** A completed spoken turn, appended to the same thread typing appends to. */
  onTurn(turn: AgentTurn): void;
  /** The browser's audio, injected for the reason `realtime-audio.ts` gives at length. */
  audio?: RealtimeAudioEnvironment;
  openSocket?: OpenVoiceSocket;
}): VoiceConversation {
  const [status, setStatus] = useState<'idle' | 'opening' | 'live'>('idle');
  const [muted, setMuted] = useState(false);
  const [error, setError] = useState('');
  const [heard, setHeard] = useState('');
  const [said, setSaid] = useState('');
  const [elapsedMs, setElapsedMs] = useState(0);

  const conversationRef = useRef<RealtimeConversation | null>(null);
  /** Whether this component is still on screen, for the setup promise below. */
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  /*
    THE CONVERSATION IS ABANDONED WHEN THIS UNMOUNTS, whatever state it was in.

    Closing the dock mid-sentence is the ordinary way out, and without this the microphone stays hot
    and the socket stays billing after the feature is visibly over — the same fault `AssistantPanel`'s
    unmount effect exists to prevent for dictation, with a running meter added. It is also why the
    dock UNMOUNTS the panel on close rather than hiding it.
  */
  useEffect(
    () => () => {
      conversationRef.current?.abandon();
      conversationRef.current = null;
    },
    [],
  );

  /*
    The clock, ticking only while there is something to count.

    A second is the right resolution for a ten-minute budget and it is what a person reads anyway; a
    faster interval would re-render the whole panel for a digit nobody can see change.
  */
  useEffect(() => {
    if (status !== 'live') return;
    const timer = setInterval(() => {
      setElapsedMs(conversationRef.current?.elapsedMs ?? 0);
    }, 1000);
    return () => clearInterval(timer);
  }, [status]);

  const ended = useCallback(
    (reason: ConversationEnd, detail?: string): void => {
      conversationRef.current = null;
      setStatus('idle');
      setMuted(false);
      setSaid('');
      setElapsedMs(0);
      setError(endingSentence(reason, detail));
    },
    [],
  );

  const start = useCallback((): void => {
    if (status !== 'idle') return;
    setStatus('opening');
    setError('');
    setHeard('');
    setSaid('');
    void RealtimeConversation.open({
      projectId,
      scenarioId,
      audio,
      openSocket,
      handlers: {
        // The partial line is REPLACED per turn, not accumulated across the conversation: it is the
        // sentence in flight, and the finished ones are in the thread above where they belong.
        onSaid: (delta) => setSaid((current) => current + delta),
        onHeard: (text) => {
          setHeard(text);
          setSaid('');
        },
        onInterrupted: () => setSaid(''),
        onTurn: (turn) => {
          /*
            STRAIGHT INTO THE SAME THREAD, which is the whole point of relaying through our own
            server. A spoken turn is an `AgentTurn` like any other, so it renders through the
            existing `AssistantThread` and its proposal through the existing `AssistantChanges` —
            what you see after speaking is what you see after typing, reviewed the same way.
          */
          setHeard('');
          setSaid('');
          onTurn(turn);
        },
        onEnded: ended,
      },
    })
      .then((conversation) => {
        /*
          A conversation that ended DURING setup is dropped rather than adopted. `open` awaits a
          microphone and then a socket; a component unmounted in between has already run its cleanup
          against a ref that was still null, so storing it here would leave a live session with
          nothing to stop it.
        */
        if (!mountedRef.current) {
          conversation.abandon();
          return;
        }
        conversationRef.current = conversation;
        setStatus('live');
        setElapsedMs(0);
      })
      .catch((cause: unknown) => {
        setStatus('idle');
        setError(cause instanceof Error ? cause.message : 'The microphone is not available.');
      });
  }, [status, projectId, scenarioId, audio, openSocket, onTurn, ended]);

  const stop = useCallback((): void => {
    conversationRef.current?.end();
  }, []);

  const toggleMute = useCallback((): void => {
    const conversation = conversationRef.current;
    if (!conversation) return;
    const next = !conversation.isMuted;
    conversation.setMuted(next);
    setMuted(next);
  }, []);

  return { status, muted, error, heard, said, elapsedMs, start, stop, toggleMute };
}

/**
 * What a live conversation says about itself, above the composer.
 *
 * Four things, each here for a reason rather than for symmetry: that it is listening, because an open
 * microphone somebody has forgotten about is the risk this feature carries; the time AGAINST the
 * ceiling, because the session will end at ten minutes and being cut off with no warning reads as a
 * fault; the mute, because a workshop has moments that are not for the model; and the partial
 * transcript, because a spoken answer that leaves no text behind gives a reader scrolling back
 * nothing until the turn is written down.
 */
export function AssistantVoiceStatus({ voice }: { voice: VoiceConversation }): JSX.Element | null {
  const { status, muted, error, heard, said, elapsedMs } = voice;
  if (status !== 'live' && !error && !heard && !said) return null;

  return (
    <div className="assistant-voice">
      {status === 'live' && (
        <div className="assistant-live" data-testid="assistant-live">
          <span className="assistant-live-dot" aria-hidden="true" />
          <span data-testid="assistant-live-state">{muted ? 'Muted' : 'Listening'}</span>
          <span className="spacer" />
          {/*
            THE BUDGET, NOT THE STOPWATCH. "3:20" alone is a number with no meaning until it stops;
            "3:20 / 10:00" says what is going to happen and when, which is the difference between a
            limit and a fault.
          */}
          <span className="muted small" data-testid="assistant-live-elapsed">
            {clock(elapsedMs)} / {clock(MAX_REALTIME_SESSION_MS)}
          </span>
          <button
            data-testid="assistant-voice-mute"
            aria-pressed={muted}
            onClick={voice.toggleMute}
            title={
              muted
                ? 'Unmute. The microphone starts sending again.'
                : 'Mute. Nothing leaves this browser until you unmute — the conversation stays open.'
            }
          >
            {muted ? 'Unmute' : 'Mute'}
          </button>
        </div>
      )}

      {(heard || said) && (
        <p className="muted small" data-testid="assistant-live-transcript">
          {heard && <span data-testid="assistant-live-heard">{heard}</span>}
          {heard && said ? ' — ' : ''}
          {said && <span data-testid="assistant-live-said">{said}</span>}
        </p>
      )}

      {error && (
        <p className="muted small" data-testid="assistant-voice-error" role="status">
          {error}
        </p>
      )}
    </div>
  );
}

/** m:ss, because a ten-minute budget read as milliseconds is a number nobody can act on. */
function clock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

/**
 * What to say when it stops, which is a different sentence for each way it can.
 *
 * A SESSION THAT SIMPLY VANISHED WOULD BE THE WORST OUTCOME. Silence after a microphone closes is
 * indistinguishable from the feature breaking, and the four involuntary endings are each something a
 * person would otherwise be left to guess at — a limit reached, a limit they did not know about, a
 * right withdrawn in another tab, or the service itself.
 *
 * `bye` is the one that says nothing, because it is the one they did on purpose.
 */
function endingSentence(reason: ConversationEnd, detail?: string): string {
  switch (reason) {
    case 'bye':
      return '';
    case 'ceiling':
      return 'That was the ten-minute limit for one conversation. Press Talk to start another.';
    case 'idle':
      return 'The conversation closed after a while with nothing said.';
    case 'evicted':
      return 'The conversation was closed because your access to this project changed.';
    case 'upstream-failed':
      return 'The assistant’s speech service stopped responding. Nothing you said was lost from the transcript above.';
    case 'refused':
      return detail || 'The conversation could not be started.';
    case 'network':
      return 'The connection dropped. Press Talk to start again.';
  }
}
